import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './Dashboard.css';

const API_URL = process.env.REACT_APP_API_URL || '';
const getToken = () => localStorage.getItem('notes_token') || '';
const fetchWithAuth = (url, options = {}) => {
  return fetch(url, { ...options, headers: { ...options.headers, 'x-token': getToken() } });
};

export default function WorkLogManager() {
  const navigate = useNavigate();
  const [logs, setLogs] = useState([]);
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [filterDate, setFilterDate] = useState(new Date().toISOString().split('T')[0]);
  const [form, setForm] = useState({
    caseId: '', recordType: '出庭', date: new Date().toISOString().split('T')[0],
    content: '', duration: '', nextStep: ''
  });

  const recordTypes = ['出庭', '会见', '文书起草', '法律检索', '客户沟通', '其他'];

  const loadData = async () => {
    setLoading(true);
    try {
      const [logsRes, casesRes] = await Promise.all([
        fetchWithAuth(`${API_URL}/api/law/logs?date=${filterDate}`),
        fetchWithAuth(`${API_URL}/api/law/cases`)
      ]);
      const logsData = await logsRes.json();
      const casesData = await casesRes.json();
      setLogs(Array.isArray(logsData) ? logsData : []);
      setCases(Array.isArray(casesData) ? casesData : []);
    } catch (e) {
      alert('加载失败: ' + e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, [filterDate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.content.trim()) return alert('请输入工作内容');
    try {
      if (editing) {
        await fetchWithAuth(`${API_URL}/api/law/logs/${editing.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(form)
        });
      } else {
        await fetchWithAuth(`${API_URL}/api/law/logs`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(form)
        });
      }
      setShowForm(false);
      setEditing(null);
      setForm({ caseId: '', recordType: '出庭', date: filterDate, content: '', duration: '', nextStep: '' });
      loadData();
    } catch (e) {
      alert('保存失败: ' + e.message);
    }
  };

  const handleEdit = (log) => {
    setEditing(log);
    setForm({
      caseId: log.caseId || '', recordType: log.recordType || '其他',
      date: log.date, content: log.content, duration: log.duration || '',
      nextStep: log.nextStep || ''
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('确定删除此记录？')) return;
    try {
      await fetchWithAuth(`${API_URL}/api/law/logs/${id}`, { method: 'DELETE' });
      loadData();
    } catch (e) {
      alert('删除失败: ' + e.message);
    }
  };

  const getCaseTitle = (caseId) => {
    const c = cases.find(x => x.id === caseId);
    return c ? c.title : '未关联案件';
  };

  return (
    <div className="work-log-manager">
      <div className="log-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>📝 工作记录</h2>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <input type="date" value={filterDate} onChange={e => setFilterDate(e.target.value)}
            style={{ padding: 6, border: '1px solid #ddd', borderRadius: 6 }} />
          <button className="btn-primary" onClick={() => {
            setEditing(null);
            setForm({ caseId: '', recordType: '出庭', date: filterDate, content: '', duration: '', nextStep: '' });
            setShowForm(true);
          }}>+ 新增记录</button>
        </div>
      </div>

      {showForm && (
        <div className="log-form-modal" style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.4)', zIndex: 1000,
          display: 'flex', justifyContent: 'center', alignItems: 'center'
        }}>
          <div style={{ background: 'white', borderRadius: 12, padding: 24, width: 500, maxHeight: '80vh', overflow: 'auto' }}>
            <h3 style={{ margin: '0 0 16px' }}>{editing ? '编辑记录' : '新增工作记录'}</h3>
            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: 12 }}>
                <label>关联案件</label>
                <select value={form.caseId} onChange={e => setForm({...form, caseId: e.target.value})}
                  style={{ width: '100%', padding: 8, border: '1px solid #ddd', borderRadius: 6 }}>
                  <option value="">未关联</option>
                  {cases.map(c => <option key={c.id} value={c.id}>{c.title}</option>)}
                </select>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                <div>
                  <label>工作类型</label>
                  <select value={form.recordType} onChange={e => setForm({...form, recordType: e.target.value})}
                    style={{ width: '100%', padding: 8, border: '1px solid #ddd', borderRadius: 6 }}>
                    {recordTypes.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label>日期</label>
                  <input type="date" value={form.date} onChange={e => setForm({...form, date: e.target.value})}
                    style={{ width: '100%', padding: 8, border: '1px solid #ddd', borderRadius: 6 }} />
                </div>
              </div>
              <div style={{ marginBottom: 12 }}>
                <label>工作内容 *</label>
                <textarea value={form.content} onChange={e => setForm({...form, content: e.target.value})}
                  rows={4} required placeholder="详细描述今日工作内容..."
                  style={{ width: '100%', padding: 8, border: '1px solid #ddd', borderRadius: 6 }} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                <div>
                  <label>耗时(小时)</label>
                  <input type="number" step="0.5" value={form.duration} onChange={e => setForm({...form, duration: e.target.value})}
                    style={{ width: '100%', padding: 8, border: '1px solid #ddd', borderRadius: 6 }} />
                </div>
                <div>
                  <label>下一步</label>
                  <input type="text" value={form.nextStep} onChange={e => setForm({...form, nextStep: e.target.value})}
                    placeholder="后续待办..." style={{ width: '100%', padding: 8, border: '1px solid #ddd', borderRadius: 6 }} />
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button type="button" onClick={() => setShowForm(false)} style={{
                  padding: '8px 16px', border: '1px solid #ddd', borderRadius: 6, background: 'white', cursor: 'pointer'
                }}>取消</button>
                <button type="submit" style={{
                  padding: '8px 16px', border: 'none', borderRadius: 6, background: '#1a73e8', color: 'white', cursor: 'pointer'
                }}>{editing ? '保存修改' : '添加记录'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>加载中...</div>
      ) : (
        <div className="log-list">
          {logs.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>
              {filterDate} 暂无工作记录
            </div>
          ) : (
            logs.map(log => (
              <div key={log.id} style={{
                background: 'white', borderRadius: 8, padding: 16, marginBottom: 12,
                border: '1px solid #e8e8e8', boxShadow: '0 1px 3px rgba(0,0,0,0.06)'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                      <span style={{
                        padding: '2px 8px', borderRadius: 4, fontSize: 12, background: '#e6f4ea', color: '#34a853'
                      }}>{log.recordType}</span>
                      <span style={{ fontSize: 13, color: '#666' }}>{getCaseTitle(log.caseId)}</span>
                      {log.duration && <span style={{ fontSize: 13, color: '#999' }}>{log.duration}h</span>}
                    </div>
                    <div style={{ fontSize: 14, lineHeight: 1.6, color: '#333', whiteSpace: 'pre-wrap' }}>
                      {log.content}
                    </div>
                    {log.nextStep && (
                      <div style={{ marginTop: 8, fontSize: 13, color: '#1a73e8' }}>
                        📌 下一步：{log.nextStep}
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 4 }}>
                    <button onClick={() => handleEdit(log)} style={{
                      border: 'none', background: 'none', color: '#1a73e8', cursor: 'pointer', fontSize: 13
                    }}>编辑</button>
                    <button onClick={() => handleDelete(log.id)} style={{
                      border: 'none', background: 'none', color: '#ea4335', cursor: 'pointer', fontSize: 13
                    }}>删除</button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
